import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Search, FileText, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const actionIcons = { created: AlertTriangle, approved: CheckCircle, rejected: XCircle, acknowledged: FileText, resolved: CheckCircle, proposed: FileText, updated: FileText };

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");

  useEffect(() => { api.get("/audit-log").then((r) => { setLogs(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const actions = [...new Set(logs.map((l) => l.action))];
  const types = [...new Set(logs.map((l) => l.entity_type))];
  const filtered = logs.filter((l) => (typeFilter === "all" || l.entity_type === typeFilter) && (actionFilter === "all" || l.action === actionFilter) && (search === "" || l.detail?.toLowerCase().includes(search.toLowerCase()) || l.actor?.toLowerCase().includes(search.toLowerCase()) || l.entity_id?.toLowerCase().includes(search.toLowerCase())));

  if (loading) return (<div className="p-6 space-y-4" data-testid="audit-log-loading"><Skeleton className="h-8 w-48 bg-white/5" />{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 bg-white/5" />)}</div>);

  return (
    <div className="p-6 space-y-4" data-testid="audit-log-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Audit & Remediation Log</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{logs.length} recorded actions</p>
      </div>
      <div className="flex flex-wrap items-center gap-3" data-testid="audit-filters">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Search logs..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 bg-black/[0.02] border-gray-200 text-[#2D3142] placeholder:text-slate-600 focus:border-cyan-500/50" data-testid="audit-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="type-filter"><SelectValue placeholder="Entity Type" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200"><SelectItem value="all">All Types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[140px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200"><SelectItem value="all">All Actions</SelectItem>{actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Badge className="text-[9px] font-mono border-0 rounded-md" style={{ background: "rgba(30,58,95,0.1)", color: "#1E3A5F" }}>{filtered.length} entries</Badge>
      </div>
      <div className="sf-card overflow-hidden" data-testid="audit-table">
        <Table className="data-table">
          <TableHeader>
            <TableRow className="bg-black/[0.01] border-b border-gray-200">
              <TableHead>Timestamp</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Entity ID</TableHead><TableHead>Actor</TableHead><TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((log, i) => {
              const Icon = actionIcons[log.action] || FileText;
              return (
                <TableRow key={log.audit_id} className="border-b border-white/[0.04] hover:bg-cyan-500/[0.03] transition-colors animate-fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid={`audit-row-${log.audit_id}`}>
                  <TableCell className="mono-data text-slate-500 whitespace-nowrap">{log.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Icon className={`w-3.5 h-3.5 ${log.action === "approved" || log.action === "resolved" ? "text-emerald-400" : log.action === "rejected" ? "text-red-400" : "text-slate-500"}`} />
                      <Badge className={`text-[9px] uppercase font-bold border-0 ${log.action === "approved" || log.action === "resolved" ? "bg-emerald-500/20 text-emerald-400" : log.action === "rejected" ? "bg-red-500/20 text-red-400" : "bg-white/10 text-slate-400"}`}>{log.action}</Badge>
                    </div>
                  </TableCell>
                  <TableCell><Badge className="text-[9px] uppercase font-mono bg-violet-500/15 text-violet-400 border-0">{log.entity_type}</Badge></TableCell>
                  <TableCell className="mono-data text-cyan-400">{log.entity_id}</TableCell>
                  <TableCell className="text-sm text-slate-400">{log.actor}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">{log.detail}</TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-600 font-mono">[ No log entries match filters ]</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
